import type {
  Coordinate,
  DeskFeature,
  DeskShapeFeature,
  EditableFloorPlan,
  EditorFeature,
} from '../models/editor-feature';
import { DEFAULT_FOOTPRINT, footprintOf, type DeskFootprint } from './desk-symbols';
import { isUsableScale, type Scale } from './measurement';
import {
  DEFAULT_GROUP_OPTIONS,
  deskGroupSeats,
  MAXIMUM_SPACING_METRES,
  type DeskGroupShape,
  type DeskSeat,
} from './desk-groups';

/**
 * Editing a group of desks after it has been stamped.
 *
 * The picker decides what a group looks like on the way down, and until now that was the only
 * moment it could be decided: a bench placed 0.4 m apart stayed 0.4 m apart, and turning a row
 * of six into two rows of three meant deleting six desks and stamping again. Deleting them is
 * not a neutral act — each one is a bookable workplace with reservations and a QR sticker
 * behind it — so the whole arrangement was in practice frozen the moment it was placed.
 *
 * So the group is re-laid out in place instead. Every desk keeps its identity and only moves,
 * which is the same thing a drag does, and the outline drawn under each desk moves with it.
 *
 * Nothing about the arrangement is stored: a pod records that its desks belong together and
 * nothing else. It is read back off the drawing each time, which costs a little inference and
 * means a pod placed by any earlier version of the editor — or nudged by hand since — is still
 * editable here rather than only the ones stamped after this was written.
 */

/** Everything the picker chooses, read back off a group already on the plan. */
export interface PodLayout {
  readonly columns: number;
  readonly rows: number;
  readonly backToBack: boolean;
  readonly gapMetres: number;
  readonly aisleMetres: number;
  /** Between the backs of rows that are back to back. Zero is a true bench, its rows touching. */
  readonly backGapMetres: number;
  /** Degrees clockwise, taken from the desks themselves so a re-layout keeps the group's facing. */
  readonly rotationDegrees: number;
  readonly footprint: DeskFootprint;
  /** What the group is laid out around, so re-laying it out leaves it where it is. */
  readonly centre: Coordinate;
}

const rotate = ([x, y]: Coordinate, degrees: number): Coordinate => {
  if (degrees === 0) {
    return [x, y];
  }

  const radians = (-degrees * Math.PI) / 180;

  return [
    x * Math.cos(radians) - y * Math.sin(radians),
    x * Math.sin(radians) + y * Math.cos(radians),
  ];
};

const centroidOf = (shape: DeskShapeFeature): Coordinate => {
  const points = shape.coordinates.flat(2);

  if (!points.length) {
    return [0, 0];
  }

  return [
    points.reduce((total, [x]) => total + x, 0) / points.length,
    points.reduce((total, [, y]) => total + y, 0) / points.length,
  ];
};

/**
 * The one group a selection is editing, or null.
 *
 * One desk of a group is enough, and is the usual case: clicking a desk selects that desk and
 * not the five beside it, so the panel has to recognise a group from any one of its seats.
 *
 * Null for anything that is not one group: a desk belonging to none, desks from two different
 * groups, a desk and a room. A control that re-arranges "the group" has to be sure which group
 * it means, and across two there is no answer.
 */
export const podIdOfSelection = (features: readonly EditorFeature[]): string | null => {
  const desks = features.filter((feature): feature is DeskFeature => feature.kind === 'desk');

  if (!desks.length || desks.length !== features.length) {
    return null;
  }

  const podIds = new Set(desks.map((desk) => desk.podId));

  return podIds.size === 1 && desks[0].podId !== null ? desks[0].podId : null;
};

export const podDesksOf = (floorPlan: EditableFloorPlan, podId: string): DeskFeature[] => floorPlan
  .desks.filter((desk) => desk.podId === podId);

/**
 * How far an outline's centre may sit from a desk and still be that desk's own image.
 *
 * Half the outline's short side, in plan units. The short side rather than the long one, so a
 * desk standing near the middle of a three-metre table does not claim the table.
 *
 * One plan unit for an outline with no readable size — on an unmeasured plan nothing can be
 * paired, which is the honest answer rather than a guess at a distance in units nobody defined.
 */
const footprintReachOf = (
  shape: DeskShapeFeature,
  desk: DeskFeature,
  scale: Scale,
): number => {
  const { widthMetres, depthMetres } = footprintOf(shape.coordinates, desk.rotation, scale);

  return isUsableScale(scale) && widthMetres > 0 && depthMetres > 0
    ? (Math.min(widthMetres, depthMetres) * scale) / 2
    : 1;
};

/**
 * The outline drawn under a desk.
 *
 * Matched by position rather than by a stored link, because there is no stored link: a desk
 * shape is an ordinary piece of furniture that happens to have been stamped with a desk. A
 * desk's outline is centred on the desk, so the nearest centroid is it — bounded by the desk's
 * own size, so a desk with no outline of its own does not adopt its neighbour's.
 *
 * Each outline is claimed once. Two desks half a metre apart would otherwise both match the
 * same one, and a re-layout would move that outline twice and leave the other desk bare.
 */
export const pairShapesToDesks = (
  floorPlan: EditableFloorPlan,
  desks: readonly DeskFeature[],
  scale: Scale,
): Map<string, DeskShapeFeature> => {
  const paired = new Map<string, DeskShapeFeature>();
  const claimed = new Set<string>();

  desks.forEach((desk) => {
    let best: DeskShapeFeature | undefined;
    let bestDistance = Infinity;

    floorPlan.deskShapes.forEach((shape) => {
      if (claimed.has(shape.id)) {
        return;
      }

      const [x, y] = centroidOf(shape);
      const distance = Math.hypot(x - desk.coordinates[0], y - desk.coordinates[1]);

      // Further than the outline's own reach and it is a different desk's outline, or a piece
      // of furniture that merely happens to sit near this one.
      const reach = footprintReachOf(shape, desk, scale);

      if (distance < bestDistance && distance <= reach) {
        best = shape;
        bestDistance = distance;
      }
    });

    if (best) {
      claimed.add(best.id);
      paired.set(desk.id, best);
    }
  });

  return paired;
};

/**
 * Distinct positions along one axis, in ascending order.
 *
 * Positions closer together than [tolerance] are one position, and each cluster reports the
 * mean of its members rather than whichever of them happened to come first. Measuring from the
 * first would let a row that drifts a few units per desk split in two, and would put the
 * recovered gap a little out on every row that is not perfectly straight.
 */
const clusterOf = (values: readonly number[], tolerance: number): number[] => {
  const sorted = [...values].sort((first, second) => first - second);
  const clusters: number[][] = [];

  sorted.forEach((value) => {
    const current = clusters[clusters.length - 1];
    const centre = current && current.reduce((total, one) => total + one, 0) / current.length;

    if (centre === undefined || Math.abs(value - centre) > tolerance) {
      clusters.push([value]);
      return;
    }

    current.push(value);
  });

  return clusters.map((cluster) => cluster.reduce((total, one) => total + one, 0) / cluster.length);
};

/** Which cluster a position belongs to, as an index into [clusters]. */
const clusterIndexOf = (clusters: readonly number[], value: number): number => clusters
  .reduce(
    (best, centre, index) => (Math.abs(value - centre) < Math.abs(value - clusters[best]) ? index : best),
    0,
  );

const medianStep = (positions: readonly number[]): number => {
  if (positions.length < 2) {
    return 0;
  }

  const steps = positions.slice(1).map((value, index) => value - positions[index]);
  const sorted = [...steps].sort((first, second) => first - second);

  return sorted[Math.floor(sorted.length / 2)];
};

const clampSpacing = (metres: number): number => {
  if (!Number.isFinite(metres) || metres <= 0) {
    return 0;
  }

  // Rounded to the centimetre. The arithmetic that recovers a gap from two drawn positions
  // lands on 0.39999999999999997, and a field that reads that back is unusable.
  return Math.min(MAXIMUM_SPACING_METRES, Math.round(metres * 100) / 100);
};

const angleGap = (first: number, second: number): number => {
  const difference = Math.abs(((first - second) % 360) + 360) % 360;
  return difference > 180 ? 360 - difference : difference;
};

/**
 * The facing a group as a whole is drawn at.
 *
 * The rotation the most of its desks share, not the first desk's. A bench's far row is turned
 * 180 degrees from the near one — the same arrangement seen from the other side — and reading
 * the group's facing off whichever desk happens to be first meant one desk turned by hand could
 * flip the whole group's reading, and with it every row and gap measured from it.
 *
 * A bench splits its desks evenly between two facings, so ties are settled by the order the
 * desks were laid out in, which puts the first row first.
 */
const groupFacingOf = (desks: readonly DeskFeature[]): number => {
  const counts = new Map<number, number>();

  desks.forEach((desk) => {
    const facing = ((desk.rotation % 360) + 360) % 360;
    counts.set(facing, (counts.get(facing) ?? 0) + 1);
  });

  return Array.from(counts.entries())
    .reduce((best, entry) => (entry[1] > best[1] ? entry : best))[0];
};

/**
 * Whether a group's rows face away from each other, which is what makes it a bench.
 *
 * Read row by row rather than desk by desk. Every desk in a row of a bench faces the same way
 * and the next row faces the other; a single desk somebody turned by hand makes its own row
 * disagree with itself, and a group with a row like that is not a bench — it is an ordinary grid
 * with one desk turned round, and snapping its rows together would be wrong.
 */
const rowsFaceApart = (
  desks: readonly DeskFeature[],
  local: readonly Coordinate[],
  rowsAt: readonly number[],
): boolean => {
  if (rowsAt.length < 2) {
    return false;
  }

  const facingPerRow = new Map<number, number>();

  for (let index = 0; index < desks.length; index += 1) {
    const row = clusterIndexOf(rowsAt, local[index][1]);
    const facing = ((desks[index].rotation % 360) + 360) % 360;
    const known = facingPerRow.get(row);

    // One row facing two ways at once: somebody has turned a desk, and this is no longer an
    // arrangement whose rows can be said to face anywhere.
    if (known !== undefined && angleGap(known, facing) > 1) {
      return false;
    }

    facingPerRow.set(row, facing);
  }

  const facings = Array.from(facingPerRow.values());

  return facings.some((facing) => angleGap(facing, facings[0]) > 90);
};

/**
 * The arrangement a group is currently drawn in.
 *
 * Read off the desks: turned back upright by the group's own facing, the seats fall into rows
 * and columns, and the distance between them less the size of a desk is the gap.
 *
 * Undefined when there is no arrangement to read — an unmeasured plan, desks drawn with no
 * outline to take a size from, or a group somebody has pulled out of line. A group that is in no
 * arrangement must not be described as being in one, because the next thing the panel would do
 * is snap every desk into the arrangement it named.
 */
export const describePod = (
  floorPlan: EditableFloorPlan,
  desks: readonly DeskFeature[],
  scale: Scale,
): PodLayout | undefined => {
  if (desks.length < 2 || !isUsableScale(scale)) {
    return undefined;
  }

  const centre: Coordinate = [
    desks.reduce((total, desk) => total + desk.coordinates[0], 0) / desks.length,
    desks.reduce((total, desk) => total + desk.coordinates[1], 0) / desks.length,
  ];

  const rotationDegrees = groupFacingOf(desks);

  const shapes = pairShapesToDesks(floorPlan, desks, scale);
  const measured = desks
    .map((desk) => shapes.get(desk.id))
    .filter((shape): shape is DeskShapeFeature => shape !== undefined)
    .map((shape) => footprintOf(shape.coordinates, rotationDegrees, scale))
    .filter((footprint) => footprint.widthMetres > 0 && footprint.depthMetres > 0);

  // A group whose desks were drawn without outlines has no size to read. Rather than invent
  // one, there is nothing here to edit: every figure the panel shows is relative to a desk.
  if (!measured.length) {
    return undefined;
  }

  const footprint: DeskFootprint = {
    widthMetres: measured.reduce((total, one) => total + one.widthMetres, 0) / measured.length,
    depthMetres: measured.reduce((total, one) => total + one.depthMetres, 0) / measured.length,
  };

  const local = desks.map((desk) => rotate(
    [desk.coordinates[0] - centre[0], desk.coordinates[1] - centre[1]],
    -rotationDegrees,
  ));

  const width = footprint.widthMetres * scale;
  const depth = footprint.depthMetres * scale;
  const columnsAt = clusterOf(local.map(([x]) => x), width / 2);
  const rowsAt = clusterOf(local.map(([, y]) => y), depth / 2);

  // A reading that does not account for every desk is not a reading. Six desks falling into
  // three columns and three rows means somebody has dragged one out of line, or deleted one
  // from the middle — and a group that is not in any arrangement must not be described as being
  // in one, because the panel's next act would be to snap all six into the arrangement it named.
  if (columnsAt.length * rowsAt.length !== desks.length) {
    return undefined;
  }

  const columns = columnsAt.length;
  const rows = rowsAt.length;

  // One measurement between two rows, read into whichever field the arrangement puts it in.
  // Naming it wrongly is what would make turning a bench into an aisled grid, or back, snap the
  // rows to a distance nobody chose.
  const isBench = rowsFaceApart(desks, local, rowsAt);
  const rowGapMetres = clampSpacing((medianStep(rowsAt) - depth) / scale);

  return {
    columns,
    rows,
    backToBack: isBench,
    gapMetres: clampSpacing((medianStep(columnsAt) - width) / scale),
    aisleMetres: isBench ? DEFAULT_GROUP_OPTIONS.aisleMetres : rowGapMetres,
    backGapMetres: isBench ? rowGapMetres : DEFAULT_GROUP_OPTIONS.backGapMetres,
    rotationDegrees,
    footprint,
    centre,
  };
};

/**
 * The order the desks of a group are laid out in.
 *
 * Row by row, each row left to right, once turned upright — the same order the seats of a new
 * group are generated in. Matching the two is what makes a re-layout move each desk to the
 * position it already held rather than shuffling the group's workplaces between seats.
 */
export const podDesksInLayoutOrder = (
  desks: readonly DeskFeature[],
  layout: PodLayout,
  scale: Scale,
): DeskFeature[] => {
  const units = isUsableScale(scale) ? scale : 1;
  const depth = layout.footprint.depthMetres * units;
  const rowGap = (layout.backToBack ? layout.backGapMetres : layout.aisleMetres) * units;
  const rowPitch = depth + rowGap;

  const keyed = desks.map((desk) => ({
    desk,
    local: rotate(
      [desk.coordinates[0] - layout.centre[0], desk.coordinates[1] - layout.centre[1]],
      -layout.rotationDegrees,
    ),
  }));

  /*
   * Which row a desk is in, as a whole number.
   *
   * Compared as row indices rather than by "is Y within half a desk of the other" — a threshold
   * is not an ordering. Three desks a third of a desk apart in Y would each count as the same
   * row as its neighbour but not as the one beyond it, and a sort given a comparator that
   * contradicts itself like that returns whatever the engine's sort happens to do. The desks
   * would come out in an order that varies between browsers, and since a re-layout pairs the
   * desks with the new seats in exactly this order, that means workplaces swapping chairs.
   */
  const rowOf = (y: number): number => (rowPitch > 0 ? Math.round(y / rowPitch) : 0);

  return keyed
    .sort((first, second) => (rowOf(first.local[1]) - rowOf(second.local[1]))
      || (first.local[0] - second.local[0]))
    .map((entry) => entry.desk);
};

/** Where the desks of a group go once it is re-laid out. Empty on a plan with no scale. */
export const podSeatsFor = (layout: PodLayout, scale: Scale): DeskSeat[] => deskGroupSeats(
  layout.centre,
  {
    kind: 'custom',
    custom: {
      columns: layout.columns,
      rows: layout.rows,
      backToBack: layout.backToBack,
    },
    footprint: layout.footprint,
    gapMetres: layout.gapMetres,
    aisleMetres: layout.aisleMetres,
    backGapMetres: layout.backGapMetres,
    rotationDegrees: layout.rotationDegrees,
  },
  scale,
);

/**
 * The arrangements a group of this many desks can be put in.
 *
 * Only the ones that use every desk: a group of six is 6x1, 3x2, 2x3 or 1x6, and never 4x2.
 * Re-laying out into a different seat count would mean creating desks that nothing has booked
 * or destroying desks that something has, and neither belongs behind a dropdown.
 */
export const arrangementsFor = (seats: number): DeskGroupShape[] => {
  const arrangements: DeskGroupShape[] = [];

  for (let columns = 1; columns <= seats; columns += 1) {
    if (seats % columns === 0) {
      arrangements.push({ columns, rows: seats / columns, backToBack: false });
    }
  }

  return arrangements;
};

/**
 * How much larger than a standard desk an outline may be and still be a desk's own image.
 *
 * Twice, in each direction. Position alone cannot answer this: a desk standing at the centre of
 * a four-metre meeting table is no further from it than from its own outline, so without a
 * bound on size, deleting that desk would delete the table. The bound errs the safe way — an
 * unusually large desk keeps its outline after the marker goes, which is a stray shape to
 * delete rather than a piece of somebody's drawing destroyed.
 */
const OUTLINE_SIZE_TOLERANCE = 2;

/**
 * A desk's outline, for the desks about to be deleted.
 *
 * A stamped desk is two features — the marker that carries the booking and the outline drawn
 * under it — and deleting only the marker left the outline behind. On one desk that is a stray
 * shape; on a group it looks as though the delete did not work at all, since six desk images
 * stay exactly where they were.
 *
 * Only an outline centred on a desk being deleted and of about a desk's size. Anything bigger is
 * furniture somebody drew that a desk happens to stand on, and deleting it is not what was asked
 * for. Held to a stricter rule than a re-layout uses, because moving the wrong outline is a
 * nudge and deleting it is not.
 */
export const outlinesOfDesks = (
  floorPlan: EditableFloorPlan,
  desks: readonly DeskFeature[],
  scale: Scale,
): DeskShapeFeature[] => {
  const paired = pairShapesToDesks(floorPlan, desks, scale);

  return desks.flatMap((desk) => {
    const outline = paired.get(desk.id);

    if (!outline) {
      return [];
    }

    const { widthMetres, depthMetres } = footprintOf(outline.coordinates, desk.rotation, scale);
    const deskSized = widthMetres <= DEFAULT_FOOTPRINT.widthMetres * OUTLINE_SIZE_TOLERANCE
      && depthMetres <= DEFAULT_FOOTPRINT.depthMetres * OUTLINE_SIZE_TOLERANCE;

    return deskSized ? [outline] : [];
  });
};
